import type { CSSProperties, MouseEvent } from "react";
import { strings } from "@/data/strings";
import { SectionHeader } from "@/components/SectionHeader";
import { AnimatedBlueBackdrop } from "@/components/AnimatedBlueBackdrop/AnimatedBlueBackdrop";
import { useInView } from "@/hooks/useInView";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import styles from "./ShowApp.module.css";

function cx(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(" ");
}

const MOCKUPS = {
  home: HomeMock,
  profile: ProfileMock,
  search: SearchMock,
} as const;

export function ShowApp() {
  const { ref, inView } = useInView<HTMLOListElement>({
    threshold: 0,
    rootMargin: "0px 0px -20% 0px",
  });

  const reducedMotion = useReducedMotion();

  function handlePointerMove(e: MouseEvent<HTMLDivElement>) {
    if (reducedMotion) return;
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    const rx = (0.5 - y) * 8;
    const ry = (x - 0.5) * 10;
    card.style.setProperty("--rx", `${rx.toFixed(2)}deg`);
    card.style.setProperty("--ry", `${ry.toFixed(2)}deg`);
  }

  function handlePointerLeave(e: MouseEvent<HTMLDivElement>) {
    const card = e.currentTarget;
    card.style.setProperty("--rx", "0deg");
    card.style.setProperty("--ry", "0deg");
  }

  return (
    <section id="app" className={styles.section}>
      <AnimatedBlueBackdrop />
      <div className="container">
        <SectionHeader
          align="center"
          eyebrow={strings.showApp.eyebrow}
          title={strings.showApp.title}
          subtitle={strings.showApp.subtitle}
        />

        <ol
          ref={ref}
          className={cx(styles.gallery, inView && styles.revealed)}>
          {strings.showApp.screens.map((screen, i) => {
            const Mockup = MOCKUPS[screen.key];
            return (
              <li
                key={screen.key}
                className={styles.item}
                style={{ "--i": i } as CSSProperties}>
                <div
                  className={styles.phone}
                  onMouseMove={handlePointerMove}
                  onMouseLeave={handlePointerLeave}
                  role="img"
                  aria-label={screen.alt}>
                  <div className={styles.screen}>
                    <Mockup />
                  </div>
                </div>

                <h3 className={styles.itemTitle}>{screen.title}</h3>
                <p className={styles.itemCaption}>{screen.caption}</p>
              </li>
            );
          })}
        </ol>

        <p className={styles.swipeHint} aria-hidden="true">
          {strings.showApp.swipeHint}
        </p>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Mockups — pantallas estilizadas. No replican toda la app, solo el "feel".
// ─────────────────────────────────────────────────────────────────────────────

const PROS = strings.hero.mockup.pros;

function HomeMock() {
  const { locationLabel, locationValue, avatarInitials, categories } =
    strings.showApp.home;

  return (
    <div className={styles.homeSurface}>
      <div className={styles.homeHeader}>
        <div className={styles.homeHeaderLoc}>
          <span className={styles.homeLocLabel}>{locationLabel}</span>
          <span className={styles.homeLocValue}>
            <IconPin />
            {locationValue}
          </span>
        </div>
        <span className={styles.homeAvatar}>{avatarInitials}</span>
      </div>

      <ul className={styles.homeCategoryList}>
        {categories.map((c) => (
          <li key={c.key} className={styles.homeCategoryCard}>
            <span className={styles.homeCategoryIcon}>
              <CategoryIcon name={c.icon} />
            </span>
            <span className={styles.homeCategoryLabel}>{c.label}</span>
          </li>
        ))}
      </ul>

      <AppTabBar />
    </div>
  );
}

function CategoryIcon({ name }: { name: "brain" | "apple" | "tooth" }) {
  if (name === "brain") return <IconBrain />;
  if (name === "apple") return <IconApple />;
  return <IconTooth />;
}

function AppTabBar() {
  const { tabBar } = strings.showApp.home;
  return (
    <div className={styles.homeTabBar}>
      <span className={cx(styles.homeTab, styles.homeTabActive)}>
        <IconHome />
        <span className={styles.homeTabLabel}>{tabBar.home}</span>
      </span>
      <span className={styles.homeTab}>
        <IconUser />
        <span className={styles.homeTabLabel}>{tabBar.profile}</span>
      </span>
    </div>
  );
}

function ProfileMock() {
  const pro = PROS[1];
  const {
    category,
    nuevoLabel,
    locationTitle,
    modalityTitle,
    modalityOnline,
    modalityPresencial,
  } = strings.showApp.profile;

  return (
    <div className={styles.profileSurface}>
      <div className={styles.profileHeader}>
        <button className={styles.profileBack} aria-hidden="true">
          <IconChevronLeft />
        </button>

        <div className={styles.profileAvatarWrap}>
          <span className={cx(styles.profileAvatar, styles.avatar1)}>
            {pro.initials}
          </span>
          <span className={styles.profileCheck} aria-hidden="true">
            <IconCheck />
          </span>
        </div>

        <h4 className={styles.profileNameLg}>{pro.name}</h4>
        <span className={styles.profileCategory}>{category}</span>
        <span className={styles.profileSpecUpper}>{pro.spec}</span>
      </div>

      <div className={styles.profileNuevoWrap}>
        <div className={styles.profileNuevoCard}>
          <span className={styles.profileNuevoLabel}>{nuevoLabel}</span>
          <IconStar />
        </div>
      </div>

      <div className={styles.profileBody}>
        <div className={styles.profileCard}>
          <h5 className={styles.profileSectionTitle}>{locationTitle}</h5>
          <div className={styles.profileMapCard}>
            <MapMendoza />
            <span className={styles.profileMapBtn} aria-hidden="true">
              <IconMap />
            </span>
          </div>
        </div>

        <div className={styles.profileCard}>
          <h5 className={styles.profileSectionTitle}>{modalityTitle}</h5>
          <ul className={styles.modalityList}>
            <li className={styles.modalityRow}>
              <span className={styles.modalityIcon}>
                <IconVideo />
              </span>
              <span className={styles.modalityLabel}>{modalityOnline}</span>
              <span
                className={cx(styles.modalitySwitch, styles.modalitySwitchOn)}
                aria-hidden="true">
                <span className={styles.modalityKnob} />
              </span>
            </li>
            <li className={styles.modalityRow}>
              <span className={styles.modalityIcon}>
                <IconHouseOutline />
              </span>
              <span className={styles.modalityLabel}>{modalityPresencial}</span>
              <span
                className={cx(styles.modalitySwitch, styles.modalitySwitchOn)}
                aria-hidden="true">
                <span className={styles.modalityKnob} />
              </span>
            </li>
          </ul>
        </div>
      </div>

      <AppTabBar />
    </div>
  );
}

// ─── Stylized map of Plaza Independencia / KM 0 Mendoza ───
function MapMendoza() {
  return (
    <svg
      viewBox="0 0 240 150"
      xmlns="http://www.w3.org/2000/svg"
      className={styles.profileMapSvg}
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true">
      <rect width="240" height="150" fill="#EFEAD7" />
      {/* Plaza Independencia (parque) */}
      <rect x="28" y="42" width="78" height="58" fill="#C8E1B0" />
      {/* Calles horizontales — Espejo (norte), Rivadavia (sur) */}
      <rect x="0" y="40" width="240" height="4" fill="#FFFFFF" />
      <rect x="0" y="100" width="240" height="4" fill="#FFFFFF" />
      <rect x="0" y="135" width="240" height="3" fill="#FFFFFF" opacity="0.85" />
      {/* Calles verticales */}
      <rect x="26" y="0" width="3.5" height="150" fill="#FFFFFF" />
      <rect x="106" y="0" width="4" height="150" fill="#FFFFFF" />
      <rect x="158" y="0" width="3.5" height="150" fill="#FFFFFF" />
      <rect x="200" y="0" width="3.5" height="150" fill="#FFFFFF" />
      <rect x="230" y="0" width="3" height="150" fill="#FFFFFF" />
      {/* Labels de calles (rotadas) */}
      <text
        x="111"
        y="76"
        fill="#3A3530"
        fontSize="5.4"
        fontWeight="700"
        textAnchor="middle"
        transform="rotate(-90 111 76)"
        letterSpacing="0.04em">
        PATRICIAS MENDOCINAS
      </text>
      <text
        x="163"
        y="76"
        fill="#3A3530"
        fontSize="5.4"
        fontWeight="700"
        textAnchor="middle"
        transform="rotate(-90 163 76)"
        letterSpacing="0.04em">
        ESPAÑA
      </text>
      <text
        x="205"
        y="76"
        fill="#3A3530"
        fontSize="5.4"
        fontWeight="700"
        textAnchor="middle"
        transform="rotate(-90 205 76)"
        letterSpacing="0.04em">
        9 DE JULIO
      </text>
      <text
        x="234"
        y="76"
        fill="#3A3530"
        fontSize="5"
        fontWeight="700"
        textAnchor="middle"
        transform="rotate(-90 234 76)"
        letterSpacing="0.04em">
        SAN MARTÍN
      </text>
      <text x="6" y="58" fill="#3A3530" fontSize="5" fontWeight="700" letterSpacing="0.04em">
        ESPEJO
      </text>
      <text x="6" y="118" fill="#3A3530" fontSize="5" fontWeight="700" letterSpacing="0.04em">
        RIVADAVIA
      </text>
      {/* Plaza label */}
      <text
        x="67"
        y="74"
        fill="#476B2F"
        fontSize="5.2"
        fontWeight="700"
        textAnchor="middle"
        letterSpacing="0.02em">
        PLAZA
      </text>
      <text
        x="67"
        y="83"
        fill="#476B2F"
        fontSize="5.2"
        fontWeight="700"
        textAnchor="middle"
        letterSpacing="0.02em">
        INDEPENDENCIA
      </text>
      {/* Pin (KM 0) */}
      <g transform="translate(120, 72)">
        <ellipse cx="0" cy="14" rx="5.5" ry="1.6" fill="rgba(0,0,0,0.18)" />
        <path
          d="M0 -10 C 5.5 -10 8 -6 8 -2 C 8 4 0 12 0 12 C 0 12 -8 4 -8 -2 C -8 -6 -5.5 -10 0 -10 Z"
          fill="#DC2626"
        />
        <circle cx="0" cy="-2" r="2.6" fill="#FFFFFF" />
      </g>
      {/* Atribución bottom-left */}
      <text x="6" y="146" fill="#3A3530" fontSize="4.5" opacity="0.7">
        Maps  ·  Plaza Independencia
      </text>
    </svg>
  );
}

function SearchMock() {
  const {
    headerTitle,
    filtersLabel,
    selectedCount,
    sheetTitle,
    sectionLabel,
    sectionHelp,
    groups,
    selected,
    clear,
    apply,
  } = strings.showApp.filters;

  return (
    <div className={styles.filtersSurface}>
      {/* Header de la pantalla de resultados (detrás del sheet) */}
      <div className={styles.filtersTopBar}>
        <span className={styles.filtersBack}>
          <IconChevronLeft />
          <span className={styles.filtersBackTitle}>{headerTitle}</span>
        </span>
        <span className={styles.filtersTrigger}>
          <IconFunnel />
          {filtersLabel} ({selectedCount})
        </span>
      </div>

      {/* Bottom sheet */}
      <div className={styles.filtersSheet}>
        <div className={styles.filtersSheetHead}>
          <span className={styles.filtersSheetTitle}>{sheetTitle}</span>
          <span className={styles.filtersClose} aria-hidden="true">
            <IconClose />
          </span>
        </div>

        <div className={styles.filtersScroll}>
          <span className={styles.filtersSectionLabel}>{sectionLabel}</span>
          <p className={styles.filtersSectionHelp}>{sectionHelp}</p>

          {groups.map((g) => (
            <div key={g.label} className={styles.filtersGroup}>
              <span className={styles.filtersGroupLabel}>{g.label}</span>
              <div className={styles.filtersChipRow}>
                {g.items.map((item) => (
                  <span
                    key={item}
                    className={cx(
                      styles.filtersChip,
                      (selected as readonly string[]).includes(item) &&
                        styles.filtersChipActive,
                    )}>
                    {item}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className={styles.filtersFooter}>
          <span className={cx(styles.filtersBtn, styles.filtersBtnGhost)}>
            {clear}
          </span>
          <span className={cx(styles.filtersBtn, styles.filtersBtnPrimary)}>
            {apply}
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── Icons (outline, currentColor) ───────────────────────────────────────────

function IconPin() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2c-3.9 0-7 3-7 6.8 0 4.6 5.5 10.4 6.3 11.2.4.4 1 .4 1.4 0 .8-.8 6.3-6.6 6.3-11.2C19 5 15.9 2 12 2Zm0 9.3a2.4 2.4 0 1 1 0-4.8 2.4 2.4 0 0 1 0 4.8Z" />
    </svg>
  );
}

function IconBrain() {
  return (
    <svg
      width="30"
      height="30"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true">
      <path d="M12 5.4C11.3 4.3 10.1 3.6 8.8 3.6c-2 0-3.6 1.5-3.6 3.4 0 .3 0 .5.1.8-1.1.5-1.8 1.5-1.8 2.8 0 .9.4 1.7 1 2.2-.2.4-.4.9-.4 1.4 0 1.6 1.4 2.9 3.1 2.9.7 1 1.8 1.7 3 1.7" />
      <path d="M12 5.4c.7-1.1 1.9-1.8 3.2-1.8 2 0 3.6 1.5 3.6 3.4 0 .3 0 .5-.1.8 1.1.5 1.8 1.5 1.8 2.8 0 .9-.4 1.7-1 2.2.2.4.4.9.4 1.4 0 1.6-1.4 2.9-3.1 2.9-.7 1-1.8 1.7-3 1.7" />
      <path d="M12 5.4V19" />
    </svg>
  );
}

function IconApple() {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true">
      <path d="M12 8.6C11 6.9 9.2 6.1 7.6 6.5 5.6 7 4 9 4 11.7c0 3.4 2.2 7.8 4.4 7.8.9 0 1.4-.5 2.2-.5h2.8c.8 0 1.3.5 2.2.5 2.2 0 4.4-4.4 4.4-7.8 0-2.7-1.6-4.7-3.6-5.2-1.6-.4-3.4.4-4.4 2.1Z" />
      <path d="M12 8.2c.1-1.7 1.4-3 3.2-3.3" />
      <circle cx="9.7" cy="12.4" r="0.7" fill="currentColor" stroke="none" />
      <circle cx="14.3" cy="12.4" r="0.7" fill="currentColor" stroke="none" />
    </svg>
  );
}

function IconTooth() {
  return (
    <svg
      width="27"
      height="27"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true">
      <path d="M8 3.2C5.5 3.2 4 5 4 7.4c0 1.6.4 3.4.9 5.6.4 1.8.6 4.3 1 6.3.2 1.1.7 1.7 1.4 1.7.8 0 1.2-.7 1.3-1.8l.5-3.7c.1-.9.4-1.4.9-1.4s.8.5.9 1.4l.5 3.7c.1 1.1.5 1.8 1.3 1.8.7 0 1.2-.6 1.4-1.7.4-2 .6-4.5 1-6.3.5-2.2.9-4 .9-5.6 0-2.4-1.5-4.2-4-4.2-1.4 0-2.4.6-4 .6s-2.6-.6-4-.6Z" />
    </svg>
  );
}

function IconFunnel() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true">
      <path d="M4 5h16l-6 7.2V19l-4 2v-8.8L4 5Z" />
    </svg>
  );
}

function IconClose() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true">
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  );
}

function IconCheck() {
  return (
    <svg
      width="9"
      height="9"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true">
      <path d="m5 12 4 4L19 6" />
    </svg>
  );
}

function IconChevronLeft() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true">
      <path d="m15 6-6 6 6 6" />
    </svg>
  );
}

function IconStar() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true">
      <path d="m12 3 2.6 6.1 6.6.6-5 4.4 1.5 6.5L12 17l-5.7 3.6 1.5-6.5-5-4.4 6.6-.6L12 3Z" />
    </svg>
  );
}

function IconMap() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true">
      <path d="m3 6 6-2 6 2 6-2v14l-6 2-6-2-6 2V6Z" />
      <path d="M9 4v16" />
      <path d="M15 6v16" />
    </svg>
  );
}

function IconVideo() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true">
      <rect x="2.5" y="6" width="13" height="12" rx="2.5" />
      <path d="m16 10 5.5-3v10L16 14" />
    </svg>
  );
}

function IconHouseOutline() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true">
      <path d="M3 11 12 4l9 7v9a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 20v-9Z" />
    </svg>
  );
}

function IconHome() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true">
      <path d="M12 3 3 11h2v9h5v-6h4v6h5v-9h2L12 3Z" />
    </svg>
  );
}

function IconUser() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
    </svg>
  );
}

